import React, { useState, useEffect } from 'react'
import { Card, Button, Space, Table, Tag, message, Typography } from 'antd'
import { getPatientLedgerTransactions, deleteAllPatientLedgerTransactions } from '../../backend-integration/api'
import { formatCurrency, getStatusColor } from '../../utils/helpers'
import dayjs from 'dayjs'

const { Text } = Typography

const DebugTransactions = ({ patientId }) => {
  const [loading, setLoading] = useState(false)
  const [transactions, setTransactions] = useState([])

  const loadAllTransactions = async () => {
    setLoading(true)
    try {
      // Load ALL transactions without any filter except entity
      const filter = {}
      if (patientId) filter.patientId = patientId
      // if (accountNo) filter.accountNo = accountNo

      console.log('DEBUG: Loading transactions with filter:', filter)
      const data = await getPatientLedgerTransactions(filter, 1, 1000, '', { date: -1 })
      console.log('DEBUG: Raw transaction data:', data)
      console.log('DEBUG: Transaction count:', data.length)
      console.log('DEBUG: Transaction types found:', [...new Set(data.map((t) => t.type))])

      setTransactions(data)
      message.info(`Loaded ${data.length} transactions`)
    } catch (error) {
      console.error('DEBUG: Error loading transactions:', error)
      message.error('Failed to load transactions')
    } finally {
      setLoading(false)
    }
  }

  const deleteAllTransactions = async () => {
    if (!window.confirm('Are you sure you want to delete ALL transactions? This cannot be undone.')) {
      return
    }

    setLoading(true)
    try {
      await deleteAllPatientLedgerTransactions()
      message.success('All transactions deleted')
      loadAllTransactions()
    } catch (error) {
      console.error('Failed to delete transactions:', error)
      message.error('Failed to delete transactions')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadAllTransactions()
  }, [patientId])

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => dayjs(date).format('DD MMM YYYY HH:mm')
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (type) => <Tag color={getStatusColor(type)}>{type}</Tag>
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: 'Debit',
      dataIndex: 'debit',
      key: 'debit',
      width: 100,
      align: 'right',
      render: (amount) => (amount > 0 ? formatCurrency(amount) : '-')
    },
    {
      title: 'Credit',
      dataIndex: 'credit',
      key: 'credit',
      width: 100,
      align: 'right',
      render: (amount) => (amount > 0 ? formatCurrency(amount) : '-')
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100
    },
    {
      title: 'Patient ID',
      dataIndex: 'patientId',
      key: 'patientId',
      width: 150,
      ellipsis: true
    },
    {
      title: 'Account No',
      dataIndex: 'billingId',
      key: 'billingId',
      width: 120
    }
  ]

  const typeStats = transactions.reduce((acc, t) => {
    acc[t.type] = (acc[t.type] || 0) + 1
    return acc
  }, {})

  return (
    <Card
      title='Debug: Transaction Database'
      extra={
        <Space>
          <Button onClick={loadAllTransactions} loading={loading}>
            Refresh
          </Button>
          <Button danger onClick={deleteAllTransactions} loading={loading}>
            Delete All
          </Button>
        </Space>
      }
    >
      <Space direction='vertical' style={{ width: '100%' }}>
        <div>
          <Text strong>Total Transactions: </Text>
          {transactions.length}
        </div>
        <div>
          <Text strong>Transaction Types: </Text>
          {Object.entries(typeStats).map(([type, count]) => (
            <Tag key={type} color={getStatusColor(type)}>
              {type}: {count}
            </Tag>
          ))}
        </div>

        <Table
          columns={columns}
          dataSource={transactions}
          rowKey='_id'
          loading={loading}
          size='small'
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1200 }}
        />
      </Space>
    </Card>
  )
}

export default DebugTransactions
